/**
 * Mobile Navigation Handler
 * Gère les interactions du menu mobile et de la recherche
 * Vanilla JavaScript avec animations fluides
 */

class MobileNav {
  constructor() {
    // Boutons
    this.menuToggle = document.querySelector("[data-menu-toggle]");
    this.searchToggles = document.querySelectorAll("[data-search-toggle]");
    this.searchClose = document.querySelector("[data-search-close]");

    // Panels
    this.menuPanel = document.querySelector("[data-menu-panel]");
    this.searchPanel = document.querySelector("[data-search-panel]");

    // États
    this.isMenuOpen = false;
    this.isSearchOpen = false;

    this.init();
    this.handleSearchSubmitAccessibility();
  }

  /**
   * Gérer l'accessibilité du bouton submit selon la taille d'écran
   */
  handleSearchSubmitAccessibility() {
    const searchSubmit = document.querySelector(
      '.site-header__search input[type="submit"], .site-header__search button[type="submit"]',
    );
    if (!searchSubmit) return;

    const updateSubmitTabIndex = () => {
      const isDesktop = window.innerWidth >= 1024;
      if (isDesktop) {
        // Desktop : bouton focusable
        searchSubmit.removeAttribute("tabindex");
      } else {
        // Mobile : bouton non focusable
        searchSubmit.setAttribute("tabindex", "-1");
      }
    };

    // Initial
    updateSubmitTabIndex();

    // Au resize
    window.addEventListener("resize", updateSubmitTabIndex);
  }

  init() {
    // Event listeners pour le menu burger
    this.menuToggle?.addEventListener("click", () => this.toggleMenu());

    // Event listeners pour tous les boutons recherche (mobile + desktop)
    this.searchToggles.forEach((toggle) => {
      toggle.addEventListener("click", () => this.toggleSearch());
    });

    // Bouton fermer la recherche
    this.searchClose?.addEventListener("click", () => this.closeSearch());

    // Fermer avec Escape
    document.addEventListener("keydown", (e) => this.handleEscape(e));

    // Fermer au clic sur overlay
    document.addEventListener("click", (e) => this.handleOutsideClick(e));

    // Gérer le resize
    window.addEventListener("resize", () => this.handleResize());
  }

  /**
   * Toggle menu mobile
   */
  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  /**
   * Ouvrir le menu mobile
   */
  openMenu() {
    this.isMenuOpen = true;

    // Fermer la recherche si ouverte
    if (this.isSearchOpen) {
      this.closeSearch();
    }

    // Animation slide-in depuis la droite
    this.menuPanel?.classList.add("is-open");
    this.menuToggle?.classList.add("is-active");
    this.menuToggle?.setAttribute("aria-expanded", "true");

    // Bloquer le scroll + overlay
    document.body.classList.add("menu-open");
    document.body.style.overflow = "hidden";

    // Focus trap pour le menu + Focus sur premier lien
    setTimeout(() => {
      this.setupMenuFocusTrap();
    }, 300);
  }

  /**
   * Fermer le menu mobile
   */
  closeMenu() {
    this.isMenuOpen = false;

    // Animation slide-out
    this.menuPanel?.classList.remove("is-open");
    this.menuToggle?.classList.remove("is-active");
    this.menuToggle?.setAttribute("aria-expanded", "false");

    // Débloquer le scroll
    document.body.classList.remove("menu-open");
    document.body.style.overflow = "";

    // Nettoyer le focus trap du menu
    if (this.menuFocusTrapCleanup) {
      this.menuFocusTrapCleanup();
      this.menuFocusTrapCleanup = null;
    }
  }

  /**
   * Toggle recherche
   */
  toggleSearch() {
    if (this.isSearchOpen) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }

  /**
   * Ouvrir la recherche
   */
  openSearch() {
    this.isSearchOpen = true;

    // Fermer le menu si ouvert
    if (this.isMenuOpen) {
      this.closeMenu();
    }

    // Animation fade-in + slide-down
    this.searchPanel?.classList.add("is-open");
    this.searchToggles.forEach((toggle) => {
      toggle.classList.add("is-active");
      toggle.setAttribute("aria-expanded", "true");
    });

    // Bloquer le scroll + overlay
    document.body.classList.add("search-open");
    document.body.style.overflow = "hidden";

    // Focus sur l'input + Focus trap
    setTimeout(() => {
      const searchInput = this.searchPanel?.querySelector(
        'input[type="search"]',
      );
      searchInput?.focus();

      // Focus trap : Tab boucle entre input et bouton fermer (loupe)
      this.setupSearchFocusTrap();
    }, 300);
  }

  /**
   * Focus trap pour la recherche (accessibilité)
   * Tab : bouton loupe ↔ input ↔ submit (boucle fermée, pas d'accès au burger)
   */
  setupSearchFocusTrap() {
    if (!this.searchPanel) return;

    const searchInput = this.searchPanel.querySelector('input[type="search"]');
    const searchSubmit = this.searchPanel.querySelector(
      'input[type="submit"], button[type="submit"]',
    );
    const searchToggle = document.querySelector("[data-search-toggle]");

    // Masquer le burger du focus quand recherche ouverte
    const menuToggle = this.menuToggle;
    if (menuToggle) {
      menuToggle.setAttribute("tabindex", "-1");
      menuToggle.setAttribute("aria-hidden", "true");
    }

    const handleTabKey = (e) => {
      if (e.key !== "Tab" || !this.isSearchOpen) return;

      // Vérifier si on est en desktop (>= 1024px)
      const isDesktop = window.innerWidth >= 1024;

      // En mobile : loupe + input uniquement
      // En desktop : loupe + input + submit
      const focusableElements = isDesktop
        ? [searchToggle, searchInput, searchSubmit].filter((el) => el)
        : [searchToggle, searchInput].filter((el) => el);

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Shift+Tab sur premier élément → dernier
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
      // Tab sur dernier élément → premier
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    // Écouter Tab
    document.addEventListener("keydown", handleTabKey);

    // Nettoyer à la fermeture
    const cleanup = () => {
      document.removeEventListener("keydown", handleTabKey);

      // Restaurer l'accessibilité du burger
      if (menuToggle) {
        menuToggle.removeAttribute("tabindex");
        menuToggle.removeAttribute("aria-hidden");
      }
    };

    // Stocker pour cleanup
    this.searchFocusTrapCleanup = cleanup;
  }

  /**
   * Fermer la recherche
   */
  closeSearch() {
    this.isSearchOpen = false;

    // Animation fade-out
    this.searchPanel?.classList.remove("is-open");
    this.searchToggles.forEach((toggle) => {
      toggle.classList.remove("is-active");
      toggle.setAttribute("aria-expanded", "false");
    });

    // Débloquer le scroll
    document.body.classList.remove("search-open");
    document.body.style.overflow = "";

    // Nettoyer le focus trap
    if (this.searchFocusTrapCleanup) {
      this.searchFocusTrapCleanup();
      this.searchFocusTrapCleanup = null;
    }
  }

  /**
   * Focus trap pour le menu mobile (accessibilité)
   * Tab : burger/fermer → Menu principal → Menu secondaire → burger/fermer (boucle complète)
   * Ordre de tabulation : respecte l'ordre visuel mobile (principal puis secondaire)
   */
  setupMenuFocusTrap() {
    if (!this.menuPanel || !this.menuToggle) return;

    // Récupérer les menus dans l'ordre VISUEL (principal en premier, secondaire en second)
    const primaryNav = this.menuPanel.querySelector(
      ".site-header__primary-nav",
    );
    const secondaryNav = this.menuPanel.querySelector(
      ".site-header__secondary-nav",
    );

    if (!primaryNav) {
      console.warn("Menu principal (.site-header__primary-nav) non trouvé");
      return;
    }

    // Récupérer TOUS les liens dans l'ordre visuel : principal PUIS secondaire
    const primaryLinks = Array.from(primaryNav.querySelectorAll("a[href]"));
    const secondaryLinks = secondaryNav
      ? Array.from(secondaryNav.querySelectorAll("a[href]"))
      : [];

    // Combiner : principal d'abord, secondaire ensuite (ordre visuel mobile)
    const allMenuLinks = [...primaryLinks, ...secondaryLinks];

    if (allMenuLinks.length === 0) {
      console.warn("Aucun lien trouvé dans les menus");
      return;
    }

    // Log initial pour debug (peut être supprimé en production)

    // Focus sur le burger (qui devient "Fermer") à l'ouverture
    this.menuToggle.focus();

    // Masquer logo et loupe du focus pendant que menu ouvert
    const logo = document.querySelector(".site-header__logo a");
    const searchToggles = document.querySelectorAll("[data-search-toggle]");

    if (logo) {
      logo.setAttribute("tabindex", "-1");
      logo.setAttribute("aria-hidden", "true");
    }

    searchToggles.forEach((toggle) => {
      toggle.setAttribute("tabindex", "-1");
      toggle.setAttribute("aria-hidden", "true");
    });

    // Flag pour éviter le double traitement
    let isProcessing = false;

    const handleTabKey = (e) => {
      if (e.key !== "Tab" || !this.isMenuOpen || isProcessing) return;

      // Verrouiller pour éviter le double traitement
      isProcessing = true;
      setTimeout(() => {
        isProcessing = false;
      }, 50);

      const currentElement = document.activeElement;

      // RECALCULER les liens à chaque Tab pour avoir les bonnes références
      const currentPrimaryNav = this.menuPanel.querySelector(
        ".site-header__primary-nav",
      );
      const currentSecondaryNav = this.menuPanel.querySelector(
        ".site-header__secondary-nav",
      );
      const currentSocialNav = this.menuPanel.querySelector(
        ".site-header__social",
      );

      const currentPrimaryLinks = currentPrimaryNav
        ? Array.from(currentPrimaryNav.querySelectorAll("a[href]"))
        : [];
      const currentSecondaryLinks = currentSecondaryNav
        ? Array.from(currentSecondaryNav.querySelectorAll("a[href]"))
        : [];
      const currentSocialLinks = currentSocialNav
        ? Array.from(currentSocialNav.querySelectorAll("a[href]"))
        : [];

      const currentFirstPrimaryLink = currentPrimaryLinks[0];
      const currentLastPrimaryLink =
        currentPrimaryLinks[currentPrimaryLinks.length - 1];
      const currentFirstSecondaryLink =
        currentSecondaryLinks.length > 0 ? currentSecondaryLinks[0] : null;
      const currentLastSecondaryLink =
        currentSecondaryLinks.length > 0
          ? currentSecondaryLinks[currentSecondaryLinks.length - 1]
          : null;
      const currentFirstSocialLink =
        currentSocialLinks.length > 0 ? currentSocialLinks[0] : null;
      const currentLastSocialLink =
        currentSocialLinks.length > 0
          ? currentSocialLinks[currentSocialLinks.length - 1]
          : null;

      // Le vraiment dernier lien : social si existe, sinon secondaire, sinon principal
      const currentLastLink =
        currentLastSocialLink ||
        currentLastSecondaryLink ||
        currentLastPrimaryLink;

      // ========== PRIORITÉ 1 : BOUCLE BURGER ↔ MENUS ==========

      // Tab depuis burger → premier lien PRINCIPAL (début de boucle)
      if (!e.shiftKey && currentElement === this.menuToggle) {
        e.preventDefault();
        currentFirstPrimaryLink?.focus();
        return;
      }

      // Shift+Tab depuis burger → dernier lien SECONDAIRE ou PRINCIPAL (fin de boucle inverse)
      if (e.shiftKey && currentElement === this.menuToggle) {
        e.preventDefault();
        currentLastLink?.focus();
        return;
      }

      // Tab depuis dernier lien SOCIAL → burger (fin de boucle si réseaux sociaux existent)
      if (
        !e.shiftKey &&
        currentLastSocialLink &&
        currentElement === currentLastSocialLink
      ) {
        e.preventDefault();
        // Différer le focus pour éviter le double déclenchement
        requestAnimationFrame(() => {
          this.menuToggle?.focus();
        });
        return;
      }

      // Tab depuis dernier lien SECONDAIRE → premier lien SOCIAL (ou burger si pas de social)
      if (
        !e.shiftKey &&
        currentLastSecondaryLink &&
        currentElement === currentLastSecondaryLink
      ) {
        e.preventDefault();
        if (currentFirstSocialLink) {
          currentFirstSocialLink?.focus();
        } else {
          // Pas de réseaux sociaux, retour au burger
          requestAnimationFrame(() => {
            this.menuToggle?.focus();
          });
        }
        return;
      }

      // Shift+Tab depuis premier lien PRINCIPAL → burger (début de boucle inverse)
      if (e.shiftKey && currentElement === currentFirstPrimaryLink) {
        e.preventDefault();
        // Différer le focus pour éviter le double déclenchement
        requestAnimationFrame(() => {
          this.menuToggle?.focus();
        });
        return;
      }

      // ========== PRIORITÉ 2 : TRANSITIONS ENTRE MENUS (forcer ordre visuel) ==========

      // Tab depuis dernier lien PRINCIPAL → premier lien SECONDAIRE
      if (
        !e.shiftKey &&
        currentFirstSecondaryLink &&
        currentElement === currentLastPrimaryLink
      ) {
        e.preventDefault();
        currentFirstSecondaryLink?.focus();
        return;
      }

      // Shift+Tab depuis premier lien SECONDAIRE → dernier lien PRINCIPAL
      if (
        e.shiftKey &&
        currentFirstSecondaryLink &&
        currentElement === currentFirstSecondaryLink
      ) {
        e.preventDefault();
        currentLastPrimaryLink?.focus();
        return;
      }

      // Shift+Tab depuis premier lien SOCIAL → dernier lien SECONDAIRE
      if (
        e.shiftKey &&
        currentFirstSocialLink &&
        currentElement === currentFirstSocialLink
      ) {
        e.preventDefault();
        currentLastSecondaryLink?.focus();
        return;
      }

      // Si aucune condition ci-dessus, laisser la navigation naturelle du DOM
    };

    // Écouter Tab
    document.addEventListener("keydown", handleTabKey);

    // Nettoyer à la fermeture
    const cleanup = () => {
      document.removeEventListener("keydown", handleTabKey);

      // Restaurer l'accessibilité du logo et loupe
      if (logo) {
        logo.removeAttribute("tabindex");
        logo.removeAttribute("aria-hidden");
      }

      searchToggles.forEach((toggle) => {
        toggle.removeAttribute("tabindex");
        toggle.removeAttribute("aria-hidden");
      });
    };

    // Stocker pour cleanup
    this.menuFocusTrapCleanup = cleanup;
  }

  /**
   * Gérer le clic en dehors
   */
  handleOutsideClick(e) {
    // Clic sur overlay (body avec classe menu-open ou search-open)
    if (e.target === document.body && (this.isMenuOpen || this.isSearchOpen)) {
      if (this.isMenuOpen) this.closeMenu();
      if (this.isSearchOpen) this.closeSearch();
      return;
    }

    // Ne rien faire si clic sur les boutons toggle
    if (
      e.target.closest("[data-menu-toggle]") ||
      e.target.closest("[data-search-toggle]") ||
      e.target.closest("[data-search-close]")
    ) {
      return;
    }

    // Fermer le menu si clic en dehors
    if (
      this.isMenuOpen &&
      this.menuPanel &&
      !e.target.closest("[data-menu-panel]")
    ) {
      this.closeMenu();
    }

    // Fermer la recherche si clic en dehors
    if (
      this.isSearchOpen &&
      this.searchPanel &&
      !e.target.closest("[data-search-panel]")
    ) {
      this.closeSearch();
    }
  }

  /**
   * Gérer la touche Escape
   */
  handleEscape(e) {
    if (e.key === "Escape") {
      if (this.isMenuOpen) this.closeMenu();
      if (this.isSearchOpen) this.closeSearch();
    }
  }

  /**
   * Gérer le resize (fermer les menus en passant en desktop)
   */
  handleResize() {
    if (window.innerWidth >= 1024) {
      if (this.isMenuOpen) this.closeMenu();
    }
  }
}

// Initialisation au chargement du DOM
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new MobileNav();
  });
} else {
  new MobileNav();
}

export default MobileNav;
